/**
 * Shopify Checkout URL Generation Utility
 * 
 * Generates checkout URLs for Colagea products with proper variant IDs.
 * Store: swkiew-cg.myshopify.com
 * 
 * Checkout link pattern: /cart/{variant_id}:{qty}[,{variant_id}:{qty}...] then optionally '?discount=CODE&ref=SOURCE'
 */

const STORE_URL = 'https://swkiew-cg.myshopify.com';

// ─── COLAGEA CURE VARIANTS ──────────────────────────────────────────────────
// Main product: Colagea Micro-Infusion Capillaire
export const CURE_VARIANTS = {
  '1_month': '53137098998033',
  '2_month': '53137099030801',
  '4_month': '53137099063569',
} as const;

// Map quantity to variant ID
export function getCureVariantId(quantity: number): string {
  switch (quantity) {
    case 1: return CURE_VARIANTS['1_month'];
    case 2: return CURE_VARIANTS['2_month'];
    case 4: return CURE_VARIANTS['4_month'];
    default: return CURE_VARIANTS['1_month'];
  }
}

// ─── FREE GIFT VARIANTS ────────────────────────────────────────────────────
// These are added to every cart as free gifts (price handled by Shopify discount/script)
export const FREE_GIFT_VARIANTS = {
  free_delivery: '53356814106897',    // Free Delivery ($9.99 value)
  derma_brush: '53356820496657',      // Derma+® Brush ($14.99 value)
  ebook: '53356814532881',            // Exclusive Ebook ($19.99 value)
  mystery_gifts: '53356821905681',    // Mystery Gifts ($10.00 value) - 4-month cure only
} as const;

// ─── LEGACY TYPE EXPORTS (backward compatibility with OrderPopup) ───────────
export type PillowSize = 'Standard' | 'King';
export type UpsellType = 'single-pillow' | 'pillowcase-set' | 'silk-pillowcase-4x' | 'silk-eyemask' | null;

// ─── LEGACY VARIANT EXPORTS (all map to Colagea cure variants) ──────────────
// These are kept so existing components (OrderPopup, etc.) don't break
export const PILLOW_VARIANTS = {
  standard_1: CURE_VARIANTS['1_month'],
  king_1: CURE_VARIANTS['1_month'],
  standard_1_upsell: CURE_VARIANTS['1_month'],
  king_1_upsell: CURE_VARIANTS['1_month'],
  standard_2: CURE_VARIANTS['2_month'],
  king_2: CURE_VARIANTS['2_month'],
  standard_4: CURE_VARIANTS['4_month'],
  king_4: CURE_VARIANTS['4_month'],
} as const;

export const DISCOUNT_CODES = {
  single_pillow_upsell: '',
  free_pillowcase_4x_standard: '',
  free_pillowcase_4x_king: '',
  silk_pillowcase_standard_4x: '',
  silk_pillowcase_king_4x: '',
  free_shipping_us_sub99: '',
} as const;

export const PILLOWCASE_SET_VARIANTS = {
  standard: { variant_id: CURE_VARIANTS['1_month'] },
  king: { variant_id: CURE_VARIANTS['1_month'] },
} as const;

export const FREE_PILLOWCASE_VARIANTS = {
  standard: CURE_VARIANTS['1_month'],
  king: CURE_VARIANTS['1_month'],
} as const;

export const SILK_PILLOWCASE_4X_VARIANTS = {
  standard: { variant_id: CURE_VARIANTS['1_month'], discount_code: '' },
  king: { variant_id: CURE_VARIANTS['1_month'], discount_code: '' },
} as const;

export const SILK_EYEMASK_UPSELL_VARIANT = {
  variant_id: CURE_VARIANTS['1_month'],
} as const;

export const SHIPPING_INSURANCE_VARIANT = {
  variant_id: '53395544146193',
  price: 5.00,
} as const;

export const HOTEL_SLIPPERS_VARIANTS: Record<string, string> = {
  'S/M': CURE_VARIANTS['1_month'],
  'L/XL': CURE_VARIANTS['1_month'],
} as const;

export const LINEN_SPRAY_VARIANT = {
  variant_id: CURE_VARIANTS['1_month'],
} as const;

// ─── TRACKING ───────────────────────────────────────────────────────────────
export const TRACKING_PARAMS = {
  utm_source: 'colagea_lp',
  utm_medium: 'colagea.manus.space',
} as const;

// ─── CHECKOUT CONFIG ────────────────────────────────────────────────────────
export interface CheckoutConfig {
  size: PillowSize;
  quantity: 1 | 2 | 4;
  upsell?: UpsellType;
  secondUpsell?: boolean;
  pillowcaseQuantity?: number;
  silkEyemask?: boolean;
  thirdUpsell?: boolean;
  shippingInsurance?: boolean;
  pageSlug?: string;
  giftSlippers?: boolean;
  slipperSize?: 'S/M' | 'L/XL';
  giftLinenSpray?: boolean;
  isUS?: boolean;
  subtotal?: number;
}

/**
 * Get the correct variant ID based on size and quantity.
 * For Colagea, size is ignored — only quantity matters.
 */
export function getPillowVariantId(_size: string, quantity: number): string {
  return getCureVariantId(quantity);
}

/**
 * Generate a Shopify checkout URL based on the cart configuration.
 * For Colagea, this maps quantity to the correct cure variant.
 */
export function generateCheckoutUrl(config: CheckoutConfig): string {
  const { quantity, secondUpsell = false, thirdUpsell = false, shippingInsurance = false, pageSlug = '' } = config;

  interface CartItem {
    variant_id: string;
    quantity: number;
  }
  const cartItems: CartItem[] = [];

  // 1. MICRO-INFUSE® upsell (if selected via any upsell slot)
  // Dedicated upsell product: MICRO-INFUSE® Hair Technology – For Men UPSELL (1-MONTH SUPPLY)
  // Variant ID: 53364242022673 ($35.90, compare at $109.90)
  if (secondUpsell || thirdUpsell) {
    cartItems.push({
      variant_id: '53364242022673',
      quantity: 1,
    });
  }

  // 2. Main cure product
  const cureVariantId = getCureVariantId(quantity);
  cartItems.push({
    variant_id: cureVariantId,
    quantity: 1,
  });

  // 3. Free gift items (added to every cart)
  cartItems.push(
    { variant_id: FREE_GIFT_VARIANTS.free_delivery, quantity: 1 },
    { variant_id: FREE_GIFT_VARIANTS.derma_brush, quantity: 1 },
    { variant_id: FREE_GIFT_VARIANTS.ebook, quantity: 1 },
  );

  // 4. Mystery Gifts (4-month cure only)
  if (quantity === 4) {
    cartItems.push({ variant_id: FREE_GIFT_VARIANTS.mystery_gifts, quantity: 1 });
  }

  // 5. Shipping Protection (if selected)
  if (shippingInsurance) {
    cartItems.push({
      variant_id: SHIPPING_INSURANCE_VARIANT.variant_id,
      quantity: 1,
    });
  }

  // Build cart URL path
  const cartPath = cartItems
    .map(item => `${item.variant_id}:${item.quantity}`)
    .join(',');

  // Build query parameters
  const queryParams: string[] = [];

  // Add discount code to auto-apply 100% off on free gift items
  queryParams.push('discount=9382FMEWG9C2');

  // Add UTM tracking
  queryParams.push(`utm_source=${TRACKING_PARAMS.utm_source}`);
  queryParams.push(`utm_medium=${TRACKING_PARAMS.utm_medium}`);

  // Add utm_campaign based on page slug
  const utmCampaign = pageSlug && pageSlug !== '/' && pageSlug !== '' ? pageSlug : 'pdp';
  queryParams.push(`utm_campaign=${utmCampaign}`);

  // Construct final URL
  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  return `${STORE_URL}/cart/${cartPath}${queryString}`;
}
